import { Plus, Upload } from "lucide-react";
import { Button } from "../../components/tailwind/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/tailwind/tabs";
import { usePageTitle } from "../../hooks/usePageTitle";
import AddContactSheet from "../../components/whatsapp/contacts/AddContactSheet";
import AttributeLibraryDialog from "../../components/whatsapp/contacts/AttributeLibraryDialog";
import ConfirmDialog, { buildDeleteDescription } from "../../components/tailwind/ConfirmDialog";
import ContactProfileSheet from "../../components/whatsapp/contacts/ContactProfileSheet";
import ContactsFilters from "../../components/whatsapp/contacts/ContactsFilters";
import ContactsTable from "../../components/whatsapp/contacts/ContactsTable";
import ImportContactsDialog from "../../components/whatsapp/contacts/ImportContactsDialog";
import SegmentsTab from "../../components/whatsapp/contacts/SegmentsTab";
import { ATTRIBUTE_LIBRARY_PLACEHOLDER, CONTACTS_PRIMARY_BUTTON_CLASS } from "./data/contactsData";
import { hasValidFilters } from "./utils/contactsUtils";
import { useContactsPage } from "./hooks/useContactsPage";
import { WhatsAppPageIntro } from "./components/WhatsAppPageIntro";
import { WA_PAGE_BG, WA_PAGE_PAD, waShell } from "./utils/whatsappPageShell";

export default function Contacts() {
  usePageTitle("Contacts");

  const {
    contacts,
    loading,
    loaded,
    error,
    hasWhatsApp,
    enrichedSegments,
    searchQuery,
    activeTab,
    filters,
    showSaveSegment,
    segmentName,
    segmentNameError,
    isImportModalOpen,
    importContactsSaving,
    isAddPanelOpen,
    isProfileOpen,
    isAttributeLibraryOpen,
    selectedContact,
    addForm,
    addFormErrors,
    addContactSaving,
    filteredContacts,
    filterOptions,
    setSearchQuery,
    handleSearchSubmit,
    handleSearchClear,
    appliedSearch,
    setIsImportModalOpen,
    setIsAddPanelOpen,
    setIsProfileOpen,
    setIsAttributeLibraryOpen,
    handleTabChange,
    addFilterRow,
    removeFilterRow,
    updateFilter,
    clearAllFilters,
    handleShowSaveSegment,
    handleSegmentNameChange,
    handleCancelSaveSegment,
    handleSaveSegment,
    handleImportSubmit,
    handleAddFormChange,
    handleOpenAddPanel,
    handleSaveContact,
    handleViewContact,
    handleDeleteContact,
    handleUpdateContact,
    handleViewSegmentContacts,
    handleDeleteSegment,
    handleUseInCampaign,
    handleCreateSegmentHint,
    pendingDelete,
    deleteConfirmLoading,
    handleCancelDelete,
    handleConfirmDelete,
    retryFetch,
    retrySegments,
    segmentsLoading,
    segmentsError,
    segmentsSaving,
    currentPage,
    totalPages,
    availablePages,
    hasNextPage,
    showPagination,
    handlePageChange,
    handlePrev,
    handleNext,
  } = useContactsPage();

  if (!hasWhatsApp) {
    return (
      <div className={waShell("w-full min-h-full", WA_PAGE_BG, WA_PAGE_PAD, "animate-fade-in")}>
        <div className="rounded-xl border border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            WhatsApp module access is required to manage contacts.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={waShell("w-full min-h-full", WA_PAGE_BG, WA_PAGE_PAD, "space-y-4 sm:space-y-6", "animate-fade-in")}>
      <WhatsAppPageIntro
        description="Manage contacts and segments for WhatsApp messaging."
        actions={
          <>
            <Button
              onClick={() => setIsImportModalOpen(true)}
              className={`w-full sm:w-auto ${CONTACTS_PRIMARY_BUTTON_CLASS}`}
            >
              <Upload className="h-4 w-4 mr-2" />
              Import Contacts
            </Button>
            <Button
              variant="outline"
              onClick={() => setIsAddPanelOpen(true)}
              className="w-full sm:w-auto border-gray-200 dark:border-slate-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Contact
            </Button>
          </>
        }
      />

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="grid w-full max-w-md grid-cols-2 mb-4 sm:mb-6 bg-gray-100 dark:bg-slate-800">
          <TabsTrigger value="all">All Contacts</TabsTrigger>
          <TabsTrigger value="segments">Segments</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4 sm:space-y-6">
          <ContactsFilters
            filters={filters}
            filteredCount={filteredContacts.length}
            totalCount={contacts.length}
            currentPage={currentPage}
            loading={loading}
            loaded={loaded}
            filterOptions={filterOptions}
            showSaveSegment={showSaveSegment}
            segmentName={segmentName}
            segmentNameError={segmentNameError}
            hasActiveFilters={hasValidFilters(filters)}
            onAddFilter={addFilterRow}
            onRemoveFilter={removeFilterRow}
            onUpdateFilter={updateFilter}
            onClearFilters={clearAllFilters}
            onShowSaveSegment={handleShowSaveSegment}
            onSegmentNameChange={handleSegmentNameChange}
            onSaveSegment={handleSaveSegment}
            segmentSaving={segmentsSaving}
            onCancelSaveSegment={handleCancelSaveSegment}
          />

          <ContactsTable
            contacts={filteredContacts}
            searchQuery={searchQuery}
            loading={loading}
            loaded={loaded}
            error={error}
            currentPage={currentPage}
            totalPages={totalPages}
            availablePages={availablePages}
            hasNextPage={hasNextPage}
            showPagination={showPagination}
            onPageChange={handlePageChange}
            onPrev={handlePrev}
            onNext={handleNext}
            onRetry={retryFetch}
            onSearchChange={setSearchQuery}
            onSearchSubmit={handleSearchSubmit}
            onSearchClear={handleSearchClear}
            hasAppliedSearch={Boolean(appliedSearch)}
            onViewContact={handleViewContact}
          />
        </TabsContent>

        <TabsContent value="segments">
          <SegmentsTab
            segments={enrichedSegments}
            loading={segmentsLoading}
            error={segmentsError}
            onRetry={retrySegments}
            onViewContacts={handleViewSegmentContacts}
            onUseInCampaign={handleUseInCampaign}
            onDeleteSegment={handleDeleteSegment}
            onCreateSegment={handleCreateSegmentHint}
          />
        </TabsContent>
      </Tabs>

      <ImportContactsDialog
        open={isImportModalOpen}
        onOpenChange={setIsImportModalOpen}
        importing={importContactsSaving}
        onSubmit={handleImportSubmit}
      />

      <AddContactSheet
        open={isAddPanelOpen}
        onOpenChange={handleOpenAddPanel}
        form={addForm}
        errors={addFormErrors}
        saving={addContactSaving}
        leadStatusOptions={filterOptions.leadStatuses}
        onChange={handleAddFormChange}
        onSave={handleSaveContact}
      />

      <ContactProfileSheet
        open={isProfileOpen}
        onOpenChange={setIsProfileOpen}
        contact={selectedContact}
        leadStatusOptions={filterOptions.leadStatuses}
        onOpenAttributeLibrary={() => setIsAttributeLibraryOpen(true)}
        onSave={handleUpdateContact}
      />

      <AttributeLibraryDialog
        open={isAttributeLibraryOpen}
        onOpenChange={setIsAttributeLibraryOpen}
        attributes={ATTRIBUTE_LIBRARY_PLACEHOLDER}
      />

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        onOpenChange={(open) => {
          if (!open) handleCancelDelete();
        }}
        title={
          pendingDelete?.type === "contact" ? "Delete contact" : "Delete segment"
        }
        description={buildDeleteDescription(
          pendingDelete?.item?.name,
          pendingDelete?.type === "contact" ? "this contact" : "this segment"
        )}
        confirmLabel={
          pendingDelete?.type === "contact" ? "Delete contact" : "Delete segment"
        }
        loading={deleteConfirmLoading}
        loadingLabel="Deleting…"
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
